import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import useLocalStorageState from "use-local-storage-state";

import { emailInRoles } from "../../../constants/selectOptions/selectOptionsLoanRoles";
import { usePermissionsHook } from "../../../hooks/usePermissionsHook";
import { nsKey } from "../../../utils/storage/partnerStorage";

/**
 * Loan-drilldown summary "view" model.
 *
 * The summary tab can render in one of several layouts. "Default" is the
 * standard view everyone sees; additional views are role-gated and let a
 * specific desk (e.g. closers) see a layout tailored to their work. The header
 * view selector (SummaryViewSelector) and the summary tab router
 * (SummaryTabViewRouter) both read this single hook so they stay in sync — the
 * selected view is persisted in partner-scoped localStorage, and
 * `use-local-storage-state` mirrors it across every hook instance in the tab.
 *
 * A view is offered to a user when EITHER they're an admin OR their role
 * matches the view's `role`. The selector only appears when the user has more
 * than just the default view available.
 *
 * Users on the closing desk (the `closers` roster in LOAN_ROLE_OPTIONS, or the
 * permissions-hook `isCloser` signal) LAND on the Closer view by default — but
 * the moment they pick a view it's persisted in partner-scoped localStorage and
 * that choice wins on every later visit.
 *
 * To add a new view: append it to SUMMARY_VIEWS (with a `role` if it's
 * role-gated), wire the matching role flag in `roleFlags` below, and render the
 * layout for its `key` in SummaryTabViewRouter.
 */
export const SUMMARY_VIEWS = [
  { key: "default", label: "Default" },
  { key: "closer", label: "Closer", role: "closer" },
];

const STORAGE_KEY = nsKey("loan-drilldown-summary-view");

export const useLoanSummaryView = () => {
  const { isAdmin, isCloser } = usePermissionsHook();
  const userEmail = useSelector((state) => state.user?.emailAddress);

  // Closing desk = the LOAN_ROLE_OPTIONS `closers` roster (what the loanCloser
  // dropdown is built from) OR the permissions-hook signal (allowlist /
  // `closing` team tag). Either makes the Closer view available and the default.
  const isCloserUser = useMemo(
    () => isCloser || emailInRoles(userEmail, ["closers"]),
    [isCloser, userEmail],
  );

  // Maps a view's `role` to whether this user satisfies it. Admins satisfy any.
  const availableViews = useMemo(() => {
    const roleFlags = { closer: isCloserUser };
    return SUMMARY_VIEWS.filter((v) => !v.role || isAdmin || roleFlags[v.role]);
  }, [isAdmin, isCloserUser]);

  // The toggle is only meaningful when there's more than the default view.
  const canSwitchView = availableViews.length > 1;

  // `null` = the user hasn't explicitly chosen a view yet, so we fall back to
  // the role-based default below. Once they pick one, it's stored and honored.
  const [storedView, setStoredView] = useLocalStorageState(STORAGE_KEY, {
    defaultValue: null,
  });

  // Where a user lands before choosing: closers → Closer, everyone else →
  // Default. Guarded by availableViews so it never points at an inaccessible view.
  const defaultView = useMemo(
    () => (isCloserUser ? "closer" : "default"),
    [isCloserUser],
  );

  const view = useMemo(() => {
    // Honor a saved choice the user can still access (remembered across visits).
    if (storedView && availableViews.some((v) => v.key === storedView)) {
      return storedView;
    }
    // No (valid) saved choice → role-based default, falling back to "default".
    return availableViews.some((v) => v.key === defaultView)
      ? defaultView
      : "default";
  }, [storedView, availableViews, defaultView]);

  const setView = useCallback(
    (next) => setStoredView(next || "default"),
    [setStoredView],
  );

  return { view, setView, availableViews, canSwitchView };
};

export default useLoanSummaryView;
